import { useRef, useState } from 'react'
import { useNavigate } from 'react-router'

import type { RecipeV2Doc, RecipeV2Summary } from '@ctcells/api'
import { PATHS } from '@ctcells/core'
import { t } from '@ctcells/i18n'

import { useSubmitValidation } from '../../atoms/useSubmitValidation.ts'
import type { DisabledState } from '../../atoms/disabled.tsx'
import { Field } from '../../atoms/Field.tsx'
import { TextInput } from '../../atoms/TextInput.tsx'
import { gate, type ScreenAccess } from '../access.ts'
import { AddActions, FormError, FormHint } from '../formSections.tsx'
import { DetailPage, OfflineNotice } from '../resourceParts.tsx'

import {
  badKeys,
  changedKeys,
  fieldId,
  fieldsOf,
  initialValues,
  type StandardModule,
} from './recipeNewStandardModel.ts'
import { RecipeDocBody, SampleTray } from '../templates/RecipeDocBody.tsx'

import '../screens.css'
import '../resource.css'
import './recipeEditor.css'

/**
 * **새 레시피 — 표준 레시피에서 시작**(eric 2026-10-12 「+레시피를 클릭하면.. 표준 레시피 기준으로 작성할 거거든? .. 바로 표준 레시피로
 * 시작하는 걸로.. 빈 레시피 처럼 나오되.. 표준 레시피의 값들이 임포트 한 상태에서 보이게」).
 *
 * ```
 * ┌ 레시피 추가 ──────────────────────────────────────────── × ┐
 * │ 기본 정보   이름 * [                                     ]   │ ← 비어 있다(새 레시피의 이름)
 * │ [원심분리기] 어셈블러 염색기 시약 랩웨어                       │
 * │ 회전 속도 (rpm) [3000]  회전 시간 (s) [180]  …               │ ← 표준 레시피 값을 불러온 채
 * │ 스텝 57                                                      │
 * │   1 c01_await_manual_load · wait_for_manual_load             │
 * │                                            [취소]  [추가]   │
 * └──────────────────────────────────────────────────────────────┘
 * ```
 *
 * 🔴 값은 서버 표준 레시피(`GET /recipes/standard`) 그대로다 — 화면이 숫자를 박지 않는다(옛 `recipeDefaults.json` 을 쓰지 않는다).
 * 🔴 칸은 공정 틀의 기본값(`process.<장비>.defaults`)만 고친다. 스텝 · 시약 · 랩웨어는 보기만 한다 — 고친 칸이 있으면 저장 전에
 *    서버가 작업 명령을 다시 만든다(컨테이너 · `POST /recipes/regenerate`). 검증도 서버다(`POST /recipes` 의 422).
 */
export interface RecipeNewStandardFormProps {
  /** `GET /recipes/standard` 본문. 받는 중이면 `undefined` */
  doc?: RecipeV2Doc | undefined
  /** `POST /recipes/summary` — 시료 자리 · 작업 차례(상세와 같은 본문 · 2026-10-13). 받는 중이면 본문을 아직 세우지 않는다 */
  summary?: RecipeV2Summary | undefined
  loading?: boolean
  loadError?: string | undefined
  access?: ScreenAccess
  onCreate?:
    ((request: { name: string; values: Readonly<Record<string, string>> }) => void) | undefined
  creating?: boolean
  createError?: string | undefined
}

export function RecipeNewStandardForm({
  doc,
  summary,
  loading = false,
  loadError,
  access = {},
  onCreate,
  creating = false,
  createError,
}: RecipeNewStandardFormProps) {
  const navigate = useNavigate()
  const rootRef = useRef<HTMLDivElement>(null)
  const [name, setName] = useState('')
  /** 고친 칸의 글자 — 손대지 않은 칸은 문서 값을 그대로 보인다 */
  const [edits, setEdits] = useState<Record<string, string>>({})
  const values = { ...initialValues(doc), ...edits }

  const edit = gate(access, { perm: 'control' })
  /* 틀린 칸 — 장비 기본값 · 작업 차례 · 시약 전부(`badKeys`) */
  const v = useSubmitValidation((d: { name: string; values: Readonly<Record<string, string>> }) => {
    const errors: Record<string, string> = {}
    if (d.name.trim() === '') errors.name = t('validation.recipeNameRequired')
    for (const key of badKeys(doc, d.values)) errors[key] = t('recipeNewStandard.badField')
    return errors
  })
  const changeValue = (key: string, text: string) => {
    setEdits((prev) => ({ ...prev, [key]: text }))
    v.change(key, { name, values: { ...values, [key]: text } })
  }
  const createGate: DisabledState = creating
    ? { disabled: true, disabledReason: t('reason.requestInFlight') }
    : onCreate === undefined
      ? { disabled: true, disabledReason: t('reason.demoSource') }
      : edit.disabled
        ? edit
        : doc === undefined
          ? { disabled: true, disabledReason: t('recipeStandard.loading') }
          : edit

  const fieldGrid = (module: StandardModule, place?: 'stages') => {
    /* 입력을 걷은 칸(`input: false`)은 세우지 않는다 — 다 걷혀 비면 그 자리째 없다(빈 칸이 남지 않는다) */
    const list = fieldsOf(doc, module, place).filter((f) => f.input !== false)
    if (list.length === 0) return null
    return (
      <div className="redit__block">
        <div className="redit__grid">
          {list.map((f) => {
            const field = v.field(fieldId(f))
            const text = values[fieldId(f)] ?? ''
            return (
              <Field {...field.fieldProps} key={fieldId(f)} label={t(f.label)}>
                <TextInput
                  {...field.inputProps}
                  inputMode={f.kind === 'int' ? 'numeric' : undefined}
                  name={fieldId(f)}
                  onChange={(text) => changeValue(fieldId(f), text)}
                  value={text}
                  wide
                  {...edit}
                />
              </Field>
            )
          })}
        </div>
      </div>
    )
  }

  const changed = changedKeys(doc, values).length
  const base = initialValues(doc)

  /*
   * 작업 차례 · 시약 칸 입력(eric 2026-10-20 「사용자가 입력하는 정하는 부분이 레서피에 잇다며.. 그거 넣어줘봐」) — 표 칸 · 시약 줄 안의 작은 입력.
   * 문서에 없는 칸이면 `undefined`(글자 그대로 · 지어내지 않는다)
   */
  const inputs = (key: string, label: string) => {
    if (!(key in base)) return undefined
    const text = values[key] ?? ''
    const field = v.field(key)
    return (
      <span className="rnstd__cell">
        <TextInput
          aria-label={label}
          {...field.inputProps}
          inputMode={key.endsWith('.lot') ? undefined : 'numeric'}
          name={key}
          onChange={(text) => changeValue(key, text)}
          /* 남김은 비우면 0(레시피 스키마 「미지정 = 0」) — 빈 칸에 0 을 옅게 보인다 */
          placeholder={key.endsWith('.dead') ? '0' : undefined}
          value={text}
          {...edit}
        />
        {field.fieldProps.error ? (
          <span className="a-field__error" id={field.fieldProps.errorId}>
            {field.fieldProps.error}
          </span>
        ) : null}
      </span>
    )
  }

  /* 「이름」 라벨 왼쪽 · 입력 칸 오른쪽(남은 폭) — eric 2026-10-13 「이름 오른쪽에 입력 창을 놔둬」(`recipeEditor.css` `.rnstd__name`) */
  const nameField = (
    <div className="rnstd__name">
      <Field {...v.field('name').fieldProps} label={t('column.name')} required>
        <TextInput
          {...v.field('name').inputProps}
          onChange={(next) => {
            setName(next)
            v.change('name', { name: next, values })
          }}
          value={name}
          wide
          {...edit}
        />
      </Field>
    </div>
  )
  return (
    <DetailPage
      actions={
        <AddActions
          busy={creating}
          error={createError}
          onCancel={() => void navigate(PATHS.recipes)}
          onSubmit={() => {
            if (!createGate.disabled && v.validate({ name, values }, rootRef.current))
              onCreate?.({ name: name.trim(), values })
          }}
          pageLabel={t('action.save')}
          submitGate={createGate}
        />
      }
      busy={creating}
      title={t('screen.recipeNew.title')}
    >
      <div ref={rootRef}>
        <OfflineNotice shown={access.offline === true} />
        <FormError message={createError} />
        {loadError === undefined ? null : (
          <p className="deny" role="alert">
            {loadError}
          </p>
        )}
        {/*
        「공통」 — 이름 + 시료 자리 트레이. 따로 묶음을 두지 않고 장비 묶음 카드 안 원심분리기 위에 선다(eric 2026-10-20 「기본 정보를 공통
        이라고 제목 붙여주고.. 공통에는.. 시료 내용을 여기다가 넣어줘」 → 「공통.. 이거. 원심분리기.. 위쪽에 넣어줘.따로 빼지 말고」).
        레시피의 시료는 «자리»(트레이 칸에 싣는 P1~P4 · REF)뿐이다 — 실제 시료는 작업을 만들 때 그 자리에 배정한다.
        본문을 못 받으면(표준 레시피 · 요약 실패) 이름 칸만 홀로 선다 — 이름을 못 적는 창이 되지 않게
      */}
        {doc === undefined || summary === undefined ? nameField : null}
        {loading && doc === undefined ? (
          <p className="muted" role="status">
            {t('recipeStandard.loading')}
          </p>
        ) : doc === undefined ? null : (
          <>
            {/*
            🔴 「표준 레시피 「…」의 값을 불러왔습니다」 한 줄은 걷었다(eric 2026-10-13 「…이 텍스트 제거해줘」).
               고친 칸 수만 고친 뒤에 한 줄로 선다(값이 표준과 갈렸다는 사실 · 종전 그 줄의 뒷부분)
          */}
            {changed === 0 ? null : (
              <FormHint>{t('recipeNewStandard.changed', { count: changed })}</FormHint>
            )}
            {/*
            🔴 레시피 상세(템플릿 › 레시피)와 같은 본문 — 트레이 그림 · 장비 묶음(기본값 · 접는 스텝) · 염색기(작업 차례 · 랩웨어 · 시약)
               (eric 2026-10-13 「레서피 추가도 마찬가지로.. 지금 레서피 상세 보기 형태로」). 기본값 자리만 입력 칸이다
          */}
            {summary === undefined ? (
              loadError !== undefined ? null : (
                <p className="muted" role="status">
                  {t('recipeStandard.loading')}
                </p>
              )
            ) : (
              <RecipeDocBody
                common={[
                  /* 묶음 제목 「공통」 · 칸 이름 「이름」(eric 2026-10-20 「이름 이름 → 공통 이름 이렇게」) */
                  { key: 'name', title: t('section.common'), node: nameField },
                  {
                    key: 'tray',
                    title: t('figTab.tray'),
                    node: <SampleTray bare doc={doc} samples={summary.samples} />,
                  },
                ]}
                doc={doc}
                fields={fieldGrid}
                inputs={inputs}
                samples={false}
                summary={summary}
              />
            )}
          </>
        )}
      </div>
    </DetailPage>
  )
}
